import { Fragment } from 'react';
import platform from 'platform';
import { sum, isEmpty } from 'lodash';
import classnames from 'classnames';
import { useToggle } from 'react-use';
import { Button, Navbar, Nav, NavItem, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

import firebase, { functions, signOut } from '../firebase';
import useDocumentSubscription from './hooks/useDocumentSubscription';
import AppButton from './AppButton';
import ModalButton from './ModalButton';
import ReferrerInfoModal from './ReferrerInfoModal';
import TenantLink from './TenantLink';
import AiSupportButton from './AiSupportButton';
import TaskButton from './TaskButton';

const sendPasswordResetEmail = functions.httpsCallable('sendPasswordResetEmail');

export default function HeaderNav(props) {
  const {
    admin = false,
    tenant,
    user,
    myAccount,
    match: { params: { tenantPath, }, path },
    location: { search },
    translate = _ => _,
  } = props;
  const tenantUser = useDocumentSubscription(user != null && tenant?.ref.collection('tenantUsers').doc(user?.id), [tenant != null, user != null]);
  const [showsReferrerInfo, toggleReferrerInfo] = useToggle(false);
  const rootPath = path.match(/^\/admin/) ? '/admin' : '/';
  const isIE = ['Microsoft Edge', 'IE'].includes(platform.name);
  const showsProfilePromotion =
    !admin && user && path !== '/mypage/profile' && !path.match(/^\/agents/) && isEmpty(user.children);
  const onClickPasswordReset = async () => {
    if (!window.confirm('パスワード変更のためのメールをお送りします。よろしいですか？')) return;

    try {
      await sendPasswordResetEmail({ email: myAccount.email });
      toast.success('パスワードリセットのメールをお送りしました。');
    } catch (e) {
      console.error(e);
      toast.error('メールの送信に失敗しました。');
    }
  };
  const onClickLogOut = async () => {
    if (!window.confirm('ログアウトしますか？')) return;
    await signOut();
    window.location.reload();
  };
  const onClickFollow = async () => {
    if (!window.confirm(`${tenant.name}の情報を受け取りますか？`)) return;

    await user.ref.update({ tenantIds: firebase.firestore.FieldValue.arrayUnion(tenantPath), });
  };

  return translate(
    <header
      className="header-nav"
      style={{ marginBottom: 54 + sum([isIE && 42, showsProfilePromotion && 49].filter((_) => _)) }}
    >
      <div className="fixed-top" style={{ boxShadow: '8.485px 8.485px 14px 0 rgb(0 0 0 / 5%)' }}>
        <Navbar color="white" light expand="md" className="justify-content-between py-2">
          <div className="d-flex align-items-center gap-1">
            {
              tenant != null ? (
                <TenantLink to={rootPath} className="navbar-brand d-flex align-items-center mr-2">
                  {admin && <span className="d-md-none font-weight-bold">VSM</span>}
                  <div className={classnames('align-items-start small', admin ? 'd-none d-md-flex' : 'd-flex')}>
                    <img src={tenant?.logo?.downloadUrl} style={{ maxHeight: 20, maxWidth: 200, }} />
                  </div>
                  {process.env.REACT_APP_ENV !== 'production' && (
                    <span className="font-weight-bold ml-3 small d-none d-md-block">【テスト環境】</span>
                  )}
                </TenantLink>
              ) : (
                <div className="d-flex align-items-center">
                  <h4 className="m-0">VSM</h4>
                  {process.env.REACT_APP_ENV !== 'production' && (
                    <span className="font-weight-bold ml-3 small d-none d-md-block">【テスト環境】</span>
                  )}
                </div>
              )
            }
            {
              tenantPath != null && user != null && !user.tenantIds?.includes(tenantPath) && (
                <Button color="link" onClick={onClickFollow}>
                  <span className="fas fa-bell text-primary" />
                </Button>
              )
            }
          </div>
          <Nav navbar className="align-items-center flex-row">
            {admin && <TaskButton user={user} />}
            <AiSupportButton user={user} />
            {user ? (
              <Fragment>
              <UncontrolledDropdown tag="div">
                <DropdownToggle color="grey" className="text-white bg-grey rounded-circle" style={{ width: 38 }}>
                  <span className="fas fa-ellipsis-v" />
                </DropdownToggle>
                <DropdownMenu right>
                  {['admin', 'staff'].includes(user.role) && (
                    <Fragment>
                      <DropdownItem tag={TenantLink} to="/admin" tenantId={user.tenantId}>
                        管理画面
                      </DropdownItem>
                      <DropdownItem tag={TenantLink} to="/admin/mypage" tenantId={user.tenantId}>
                        管理マイページ
                      </DropdownItem>
                      <DropdownItem onClick={() => toggleReferrerInfo(true)}>リファラ内容確認</DropdownItem>
                    </Fragment>
                  )}
                  {tenantUser?.salesStatReadble && (
                    <DropdownItem tag={TenantLink} to="/salesStat">
                      販売統計
                    </DropdownItem>
                  )}
                  <DropdownItem tag={Link} to="/mypage/profile">
                    プロフィール
                  </DropdownItem>
                  <DropdownItem onClick={onClickPasswordReset}>パスワード変更</DropdownItem>
                  <DropdownItem tag={Link} to="/mypage">
                    マイページ
                  </DropdownItem>
                  <DropdownItem tag={Link} to="/mypage/orders">
                    注文履歴
                  </DropdownItem>
                  <DropdownItem onClick={onClickLogOut}>ログアウト</DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              {['admin', 'staff'].includes(user.role) && (
                <ReferrerInfoModal
                  tenant={tenant}
                  tenantPath={tenantPath}
                  user={user}
                  isOpen={showsReferrerInfo}
                  onClose={() => toggleReferrerInfo(false)}
                />
              )}
              </Fragment>
            ) : (
              <NavItem>
                <TenantLink
                  to="/login"
                  className="nav-link"
                >
                  ログイン
                </TenantLink>
              </NavItem>
            )}
          </Nav>
        </Navbar>
        {isIE && (
          <div className="alert alert-danger py-2 mb-0">
            お使いのブラウザは推奨環境ではありません。
            <a href="https://www.google.com/intl/ja_jp/chrome/" target="_blank">
              Google Chrome
            </a>
            のご利用をおすすめいたします。
          </div>
        )}
        {showsProfilePromotion && (
          <div className="alert alert-secondary text-dark py-2 mb-0" style={{ fontSize: '1rem' }}>
            <span className="fas fa-child mr-1" />
            お子様に合わせた乗り方サポート
            <ModalButton
              label="詳しく"
              title="お子様に合わせた乗り方サポート"
              outline
              className="ml-3"
              color="primary"
              size="sm"
              content={(_) => {
                return (
                  <div>
                    <div>
                      お子様に合わせた乗り方サポートをしています。
                      <br />
                      お子様について教えてください。
                    </div>
                    <div className="mt-3 d-flex justify-content-center">
                      <AppButton outline color="primary" tag={TenantLink} to={`/mypage/profile`}>
                        編集する
                      </AppButton>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        )}
      </div>
    </header>
  );
}
